import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  LinkerPayloadLeavingGroupsQuery,
  LinkerPayloadLeavingGroupsQueryVariables,
} from 'molbio/adcs/linker-payload-connection/__generated/LinkerPayloadLeavingGroupsQuery';
import {
  getLinkerPayloadLeavingGroupsResult,
  LINKER_PAYLOAD_LEAVING_GROUPS_QUERY,
  LinkerPayloadLeavingGroupsResult,
} from 'molbio/adcs/linker-payload-connection/linkerPayloadLeavingGroupsQuery';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {
  findColumnIndexByGroup,
  getBioentityIdFromEvaluatedCell,
} from 'notebook/tables/search-scoping/rowScopedSearch';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {assertNever} from 'util/Types';

export type LinkerPayloadLeavingGroupsState =
  /** The row has no linker-payload. */
  | {status: 'noLinkerPayload'}
  | {status: 'loading'}
  /** The leaving groups query failed. */
  | {status: 'queryError'}
  | {status: 'loaded'; result: LinkerPayloadLeavingGroupsResult};

/**
 * Loads the leaving groups on the row's Linker-payload molecule (see getLinkerPayloadLeavingGroupsResult). Must be
 * called inside a table row (see RowModelProvider).
 */
export default function useLinkerPayloadLeavingGroups(): LinkerPayloadLeavingGroupsState {
  const moleculeId = useRowModel(({columnConstraints, evaluatedRow}) =>
    getBioentityIdFromEvaluatedCell(
      evaluatedRow[findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_LINKER_PAYLOAD)]
    )
  );
  const queryResult = useSkippableBenchlingQuery<
    LinkerPayloadLeavingGroupsQuery,
    LinkerPayloadLeavingGroupsQueryVariables
  >(LINKER_PAYLOAD_LEAVING_GROUPS_QUERY, {
    variables: {moleculeId: moleculeId ?? ''},
    skip: moleculeId == null,
  });
  switch (queryResult.type) {
    case QueryResultType.SKIPPED:
      return {status: 'noLinkerPayload'};
    case QueryResultType.PENDING:
      return {status: 'loading'};
    case QueryResultType.ERROR:
      return {status: 'queryError'};
    case QueryResultType.SUCCESS:
      return {status: 'loaded', result: getLinkerPayloadLeavingGroupsResult(queryResult.data)};
    default:
      return assertNever(queryResult);
  }
}
